import React from 'react';
import { ExternalLink } from 'lucide-react';

interface CaseStudyItem {
  client: string;
  url?: string;
  domainLabel?: string;
  location: string;
  industry: string;
  workDone?: string[];
  challenge: string;
  solution: string;
  metrics: { label: string; value: string }[];
  quote: string;
  author: string;
  featured?: boolean;
}

export const CaseStudies: React.FC = () => {
  const cases: CaseStudyItem[] = [
    {
      client: 'Isuroon',
      url: 'https://isuroon.org/',
      domainLabel: 'isuroon.org',
      location: 'Minnesota, USA & Global Impact',
      industry: 'Non-Profit · Community Health & Advocacy',
      workDone: ['UI/UX Architecture', 'Schema.org Entity Graph', 'SEO', 'GEO'],
      featured: true,
      challenge:
        'As a mission-driven organization advancing health equity, economic opportunity, and civic advocacy for women and families, Isuroon needed structured AI search discoverability so institutional funders, partners, and community members could immediately find verified program information.',
      solution:
        'Engineered and benchmarked against the Xweba SEO + GEO Standard with a 7-node Schema.org entity graph (CommunityHealth & Organization), clear answer-ready program pages, and low-friction interactive community & donor engagement pathways.',
      metrics: [
        { label: 'GEO Visibility', value: '92%' },
        { label: 'Schema Completeness', value: '86%' },
        { label: 'Readiness Score', value: '84/100' },
      ],
      quote:
        'Our digital presence now communicates our community health initiatives, advocacy work, and measurable impact with clarity—making Isuroon easily discoverable across both Google and modern AI research platforms.',
      author: 'Leadership Team · isuroon.org',
    },
    {
      client: 'Golden Wheels Kenya',
      url: 'https://goldenwheels.co.ke/',
      domainLabel: 'goldenwheels.co.ke',
      location: 'Nairobi, Kenya',
      industry: 'Automotive & Mobility Solutions',
      workDone: ['UI/UX', 'Graphic Designing', 'SEO', 'GEO'],
      featured: true,
      challenge:
        'Needed a modern, high-converting digital storefront with cohesive visual branding, intuitive mobile-first vehicle browsing, and strong organic and AI search visibility across Kenya so prospective buyers and clients could discover and inquire effortlessly.',
      solution:
        'Xweba delivered a complete end-to-end transformation covering bespoke UI/UX architecture, custom Graphic Designing and brand assets, technical on-page SEO, and Generative Engine Optimization (GEO) structured for Google and AI search engines.',
      metrics: [
        { label: 'UI/UX & Brand', value: '100%' },
        { label: 'SEO & GEO Score', value: '91%' },
        { label: 'Inbound Inquiries', value: '+210%' },
      ],
      quote:
        'From the custom UI/UX and graphic design identity to ranking across Google and AI search engines through SEO and GEO, Xweba elevated Golden Wheels into a standout digital brand.',
      author: 'Management Team · goldenwheels.co.ke',
    },
    {
      client: 'Vanguard Legal & Advisory',
      location: 'Nairobi & East Africa',
      industry: 'Corporate Law & Commercial Contracts',
      workDone: ['AI Web Platform', 'SEO & GEO', 'WhatsApp & CRM Automation'],
      challenge:
        'Their legacy WordPress website ranked for a few obsolete keywords, but was completely invisible on ChatGPT and Perplexity. Inbound weekend contact inquiries went unanswered for 48+ hours.',
      solution:
        'Xweba engineered an AI Pro platform featuring verified Schema.org entity graphs, question-based legal guides, and a 24/7 conversational lead qualification agent synced directly to WhatsApp & CRM.',
      metrics: [
        { label: 'AI Search Citations', value: '+340%' },
        { label: 'Speed to Lead', value: '45 sec' },
        { label: 'Qualified Consultations', value: '+72%' },
      ],
      quote:
        'Xweba transformed our website from an expense into our most profitable business acquisition channel. Prospective corporate clients literally mention that ChatGPT recommended us.',
      author: 'David Mwangi, Managing Partner',
    },
  ];

  return (
    <section id="proof" className="py-14 sm:py-20 lg:py-24 bg-white border-b border-slate-200 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center space-y-3 sm:space-y-4 mb-10 sm:mb-14 lg:mb-16">
          <div className="inline-flex flex-wrap items-center justify-center gap-1.5 sm:gap-2 text-[11px] sm:text-xs font-bold tracking-wider uppercase text-slate-500">
            <span className="text-[#F26522] font-black">PROVEN RESULTS</span>
            <span aria-hidden="true" className="text-slate-300">
              ·
            </span>
            <span>2026 CASE STUDIES</span>
          </div>

          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold text-[#0B2D52] tracking-tight leading-tight">
            Real businesses. Real AI citations. Real revenue.
          </h2>

          <p className="text-slate-600 text-sm sm:text-base lg:text-lg max-w-2xl mx-auto leading-relaxed">
            See how forward-thinking organizations upgraded their digital architecture to get found,
            understood, and chosen.
          </p>
        </div>

        {/* Case Studies Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 lg:gap-8">
          {cases.map((cs, idx) => (
            <div
              key={idx}
              className={`min-w-0 w-full rounded-2xl sm:rounded-3xl p-5 sm:p-6 xl:p-7 flex flex-col justify-between transition-all shadow-2xs border ${
                cs.featured
                  ? 'bg-gradient-to-br from-orange-50/60 via-white to-slate-50 border-orange-300 ring-1 ring-orange-200/70'
                  : 'bg-slate-50 border-slate-200 hover:border-slate-300'
              }`}
            >
              <div className="space-y-4 sm:space-y-5 min-w-0">
                {/* Meta details */}
                <div className="space-y-2 min-w-0">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider leading-snug min-w-0 flex-1">
                      <span className="text-[#F26522]">{cs.industry}</span>
                      <span className="mx-1 text-slate-300">·</span>
                      <span className="text-slate-500">{cs.location}</span>
                    </div>
                    {cs.url && (
                      <a
                        href={cs.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-2.5 py-1 rounded-lg bg-white hover:bg-[#0B2D52] text-[#0B2D52] hover:text-white border border-slate-200 text-[11px] font-bold inline-flex items-center gap-1 transition-colors shrink-0 whitespace-nowrap shadow-2xs"
                      >
                        <span>
                          {cs.domainLabel ||
                            cs.url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
                        </span>
                        <ExternalLink className="w-3 h-3 shrink-0" />
                      </a>
                    )}
                  </div>

                  <h3 className="text-lg sm:text-xl xl:text-[22px] font-black text-[#0B2D52] tracking-tight leading-snug whitespace-nowrap">
                    {cs.url ? (
                      <a
                        href={cs.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-[#F26522] transition-colors inline-flex items-center gap-1.5 sm:gap-2 underline decoration-slate-300 hover:decoration-[#F26522] underline-offset-4 whitespace-nowrap"
                      >
                        <span>{cs.client}</span>
                        <ExternalLink className="w-4 h-4 text-[#F26522] shrink-0" />
                      </a>
                    ) : (
                      <span>{cs.client}</span>
                    )}
                  </h3>
                </div>

                {/* Work Done / Scope of Delivery */}
                {cs.workDone && cs.workDone.length > 0 && (
                  <div className="pt-0.5">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                      Work Done
                    </div>
                    <div className="flex flex-wrap items-center gap-x-1.5 sm:gap-x-2 gap-y-1 text-[11px] sm:text-xs font-bold text-[#0B2D52] leading-snug">
                      {cs.workDone.map((item, wIdx) => (
                        <React.Fragment key={wIdx}>
                          {wIdx > 0 && (
                            <span aria-hidden="true" className="text-[#F26522]">
                              ·
                            </span>
                          )}
                          <span>{item}</span>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                )}

                {/* Metrics ribbon */}
                <div className="grid grid-cols-3 gap-1 sm:gap-2 bg-white p-2.5 sm:p-3.5 rounded-xl sm:rounded-2xl border border-slate-200 text-center tabular-nums divide-x divide-slate-100">
                  {cs.metrics.map((m, mIdx) => (
                    <div key={mIdx} className="px-1 flex flex-col justify-center min-w-0">
                      <div className="text-base sm:text-lg xl:text-xl font-black text-[#F26522] leading-none">
                        {m.value}
                      </div>
                      <div className="text-[10px] sm:text-[11px] text-slate-500 font-medium leading-tight mt-1 break-words">
                        {m.label}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Challenge & Solution */}
                <div className="space-y-2.5 sm:space-y-3 text-xs sm:text-sm text-slate-600 leading-relaxed">
                  <div>
                    <strong className="text-slate-900 block mb-0.5">The Bottleneck:</strong>
                    {cs.challenge}
                  </div>
                  <div>
                    <strong className="text-slate-900 block mb-0.5">Xweba Architecture:</strong>
                    {cs.solution}
                  </div>
                </div>
              </div>

              {/* Client Quote */}
              <div className="pt-4 sm:pt-5 mt-5 sm:mt-6 border-t border-slate-200/80 space-y-2.5 sm:space-y-3">
                <p className="text-xs sm:text-sm italic text-slate-700 leading-relaxed font-medium">
                  "{cs.quote}"
                </p>
                <div className="flex flex-wrap items-center justify-between gap-2 pt-0.5">
                  <div className="text-[11px] font-bold text-[#0B2D52] leading-snug">
                    — {cs.author}
                  </div>
                  {cs.url && (
                    <a
                      href={cs.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-bold text-[#F26522] hover:text-[#ea580c] inline-flex items-center gap-1 py-1 whitespace-nowrap"
                    >
                      <span>Visit Live Site</span>
                      <ExternalLink className="w-3 h-3 shrink-0" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
